import { useCallback, useEffect, useState } from 'react';
import { formatPercent, formatRial, indexDriverLabel, type IndexFactorSource } from '@kia/shared';
import { api, errMsg } from '../api/client';
import { useApiResource } from '../lib/useApiResource';
import { useMarginFloors } from '../config/ConfigProvider';
import { marginColor } from '../lib/margin';
import { pricingFor } from '../lib/pricing';
import {
  Button,
  Card,
  EntityPicker,
  ErrorRetry,
  Field,
  Modal,
  NumberInput,
  PricingHint,
  Sparkline,
  useConfirm,
  useToast,
} from '../ui';
import { CsvImportModal } from './CsvImportModal';

/**
 * Market section of the Prices screen (migration 005): the USD/IRR register that leads material
 * indexation, the blended-indexation card, and the official sales/market price list with the
 * max-discount guard. All history is graphable via the shared HistoryModal.
 */

interface FxRate {
  day: string;
  usd_irr: number;
}
interface HistoryPoint {
  ts: string;
  value: number;
}
// BIDX-04 — one driver group|unit's contribution to the basket move (group-price plane). Inlined here (NOT
// imported from the server) to avoid crossing the @kia/shared→dist boundary; mirrors the server BasketLeg shape.
interface BasketLeg {
  key: string;
  name: string;
  unit: string;
  weight: number;
  move: number;
  lastMoveAgeDays: number;
  priced: boolean;
}
interface Indexation {
  windowDays: number;
  /** R1 — false until the go-live baseline date is set; indexation is off (no apply) until then. */
  enabled: boolean;
  /** R1 — the go-live / pricing-baseline date ('' when unset). */
  baselineDate: string;
  /** R1 — the raw auto signal exceeded the sane ceiling ⇒ auto-apply blocked, manual override only. */
  abnormal: boolean;
  usdChg: number | null;
  avgMatChg: number | null;
  blended: number | null;
  maxFactor: number | null;
  stale: {
    code: string;
    price: number;
    date: string;
    group: string;
    factor: number;
    suggested: number;
  }[];
  /** BIDX-04 — the GATED driver-basket move g_B (β applies to the tail); null ⇒ FX/material fallback. */
  basketRate: number | null;
  /** BIDX-04/06 — the β lever damping the tail bump (β·g_B). */
  basketBeta: number;
  /** BIDX-04 — covered-driver base-value slice of the driver basket. */
  basketCoverage: number;
  /** BIDX-04 — number of covered drivers (a genuine in-window group-plane move). */
  basketDriverCount: number;
  /** BIDX-04 — driver-basket base-value share of the whole book. */
  driverShare: number;
  /** BIDX-04 — laggy-tail base-value share (1 − driverShare). */
  tailShare: number;
  /** BIDX-04 — the covered driver legs (weight·move reconciles to g_B). */
  basketLegs: BasketLeg[];
  /** BIDX-04 — driver legs whose last genuine move is ≥ the move window. */
  driverStale: string[];
  /** BIDX-04 — which signal drove the headline factor. */
  factorSource: IndexFactorSource;
  /**
   * PREPROD-1 — the SECOND money plane. `applyIndexation` writes stale non-driver GROUP buckets in the same
   * tx as the mp rows, and a group price is the COGS basis for every material with no mp row — i.e. most of
   * the tail. This card previously did not know the field existed: it counted, previewed, confirmed and
   * GATED on `stale` alone, so in the book BIDX-05 targets (empty mp plane, full group plane) it showed a
   * false zero and rendered no apply button at all while the weekly bell — rewired onto the union — told the
   * owner to press it. Both planes are disclosed below, and never merged: a material row is a single code,
   * a group row is a whole bucket whose bump moves every unpriced material inside it.
   */
  staleGroups: {
    /** `gpKey(group, unit)` — «گروه|واحد». */
    key: string;
    price: number;
    date: string;
    factor: number;
    suggested: number;
  }[];
}
interface SalesPriceRow {
  product_code: string;
  name: string;
  list_price: number;
  max_discount: number;
  date: string;
}
interface ProductHit {
  code: string;
  name: string;
  fullCost: number;
  listPrice: number;
  maxDiscount: number;
  floor: number;
  target: number;
  suggestedAtFloor: number;
}

export function HistoryModal({
  kind,
  keyName,
  title,
  onClose,
}: {
  kind: 'fx' | 'sales' | 'material' | 'group';
  keyName: string;
  title: string;
  onClose: () => void;
}) {
  const [points, setPoints] = useState<HistoryPoint[] | null>(null);
  useEffect(() => {
    void api<HistoryPoint[]>(
      `/api/market/history?kind=${kind}&key=${encodeURIComponent(keyName)}`,
    ).then((r) => {
      if (r.ok && Array.isArray(r.body)) setPoints(r.body as HistoryPoint[]);
    });
  }, [kind, keyName]);
  return (
    <Modal title={`تاریخچهٔ ${title}`} onClose={onClose}>
      {!points ? (
        <p className="muted">در حال بارگذاری…</p>
      ) : points.length < 2 ? (
        <p className="muted">هنوز تغییری ثبت نشده — با اولین به‌روزرسانی، نمودار ساخته می‌شود.</p>
      ) : (
        <>
          <Sparkline points={points.map((p) => p.value)} width={520} height={140} />
          <table className="table" style={{ marginTop: 10 }}>
            <thead>
              <tr>
                <th>تاریخ</th>
                <th>مقدار (﷼)</th>
              </tr>
            </thead>
            <tbody>
              {points
                .slice(-12)
                .reverse()
                .map((p, i) => (
                  <tr key={i}>
                    <td className="num">{p.ts}</td>
                    <td className="num">{formatRial(p.value)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </>
      )}
    </Modal>
  );
}

export function MarketCards({ canEdit }: { canEdit: boolean }) {
  const toast = useToast();
  const confirm = useConfirm();
  const floors = useMarginFloors();

  // --- FX ---
  const [fx, setFx] = useState<{ latest: FxRate | null; history: FxRate[] } | null>(null);
  const [rateDraft, setRateDraft] = useState('');
  const [fxBusy, setFxBusy] = useState(false);
  const [historyOf, setHistoryOf] = useState<{
    kind: 'fx' | 'sales';
    key: string;
    title: string;
  } | null>(null);

  const loadFx = useCallback(() => {
    void api<typeof fx>('/api/market/fx').then((r) => {
      if (r.ok) setFx(r.body as typeof fx);
    });
  }, []);
  useEffect(loadFx, [loadFx]);

  async function saveFx() {
    setFxBusy(true);
    const r = await api<typeof fx>('/api/market/fx', {
      method: 'POST',
      body: JSON.stringify({ usd_irr: Number(rateDraft) }),
    });
    setFxBusy(false);
    if (!r.ok) return toast(errMsg(r), 'error');
    setFx(r.body as typeof fx);
    setRateDraft('');
    toast('نرخ دلار ثبت شد');
    void loadIndexation(windowDays);
  }

  // --- Indexation ---
  const [windowDays, setWindowDays] = useState(30);
  const [idx, setIdx] = useState<Indexation | null>(null);
  const [factorDraft, setFactorDraft] = useState('');
  const [idxBusy, setIdxBusy] = useState(false);

  const loadIndexation = useCallback(async (w: number) => {
    const r = await api<Indexation>(`/api/market/indexation?window=${w}`);
    if (r.ok) {
      const v = r.body as Indexation;
      setIdx(v);
      setFactorDraft(v.blended != null ? v.blended.toFixed(3) : '');
    }
  }, []);
  useEffect(() => {
    void loadIndexation(windowDays);
  }, [windowDays, loadIndexation]);

  async function applyIndexation(uniform: boolean) {
    if (!idx) return;
    // Default: index each material by the dollar move (send no factor). Uniform: force the drafted factor.
    const factor = Number(factorDraft);
    if (uniform && (!Number.isFinite(factor) || factor <= 0)) {
      return toast('برای اعمال یکنواخت، ضریب معتبر وارد کنید', 'error');
    }
    // BIDX-07 / PREPROD-1 — name the ACTUAL basis the server used, via the ONE shared authority. The old
    // binary `basket ? سبد : دلار` collapse of a five-valued field asserted «حرکت دلار» for the material,
    // override and none sources too.
    const basis = indexDriverLabel(idx.factorSource);
    // PREPROD-1 — the confirm must count BOTH planes the apply writes. It counted `stale` (materials) only,
    // so on a group-priced tail the owner confirmed «۰ ماده» and every group bucket moved anyway.
    const nMat = idx.stale.length;
    const nGrp = idx.staleGroups.length;
    const scope = `${nMat} قیمت اختصاصی ماده و ${nGrp} قیمت گروه (که مبنای بهای همهٔ مواد بدون قیمت اختصاصی است)`;
    const msg = uniform
      ? `${scope} در ضریب ثابت ${(1 + factor).toFixed(3)} ضرب می‌شود. ادامه؟`
      : `${scope} بر اساس ${basis} به‌روز می‌شود. ادامه؟`;
    if (!(await confirm({ title: 'به‌روزرسانی شاخصی قیمت‌ها', message: msg, danger: true })))
      return;
    setIdxBusy(true);
    const r = await api<{ updated: number }>('/api/market/indexation', {
      method: 'POST',
      body: JSON.stringify({ window: windowDays, ...(uniform ? { factor } : {}) }),
    });
    setIdxBusy(false);
    if (!r.ok) return toast(errMsg(r), 'error');
    // PREPROD-1 — the server's `updated` is `changed.length + changedGroups.length` (market.ts), a MIXED
    // count. Labelling it «ماده» understated nothing but named the wrong object: on a group-priced tail
    // every one of those was a group bucket. «نرخ (ماده/گروه)» is the same vocabulary the weekly bell uses.
    toast(`${(r.body as { updated: number }).updated} نرخ (ماده/گروه) به‌روزرسانی شد`);
    void loadIndexation(windowDays);
  }

  // R1 — declare "initial pricing complete": set the go-live baseline to today and enable indexation.
  async function setBaseline() {
    if (
      !(await confirm({
        title: 'ثبت تاریخ مبنای قیمت',
        message:
          'با این کار «تاریخ مبنای قیمت» برابر امروز ثبت و به‌روزرسانی شاخصی فعال می‌شود. از این پس فقط تغییرات قیمتِ ثبت‌شده پس از امروز به‌عنوان سیگنال بازار در نظر گرفته می‌شوند (ثبت‌های اولیهٔ مبناگذاری نادیده گرفته می‌شوند). ادامه؟',
        danger: true,
      }))
    )
      return;
    setIdxBusy(true);
    const r = await api<{ baselineDate: string }>('/api/market/indexation/baseline', {
      method: 'POST',
      body: JSON.stringify({}),
    });
    setIdxBusy(false);
    if (!r.ok) return toast(errMsg(r), 'error');
    toast('تاریخ مبنای قیمت ثبت شد — شاخص‌سازی فعال شد');
    void loadIndexation(windowDays);
  }

  // --- Sales list ---
  const {
    data: salesData,
    status: salesStatus,
    error: salesError,
    reload: loadSales,
  } = useApiResource<SalesPriceRow[]>('/api/market/prices');
  const sales = salesData ?? [];
  const [pick, setPick] = useState<ProductHit | null>(null);
  const [listDraft, setListDraft] = useState('');
  const [discDraft, setDiscDraft] = useState('');
  const [salesBusy, setSalesBusy] = useState(false);
  const [salesImportOpen, setSalesImportOpen] = useState(false);

  async function saveSales() {
    if (!pick) return;
    setSalesBusy(true);
    const r = await api('/api/market/prices', {
      method: 'POST',
      body: JSON.stringify({
        product_code: pick.code,
        list_price: Number(listDraft),
        max_discount: Number(discDraft || 0) / 100,
      }),
    });
    setSalesBusy(false);
    if (!r.ok) return toast(errMsg(r), 'error');
    toast('قیمت لیست ذخیره شد');
    setPick(null);
    setListDraft('');
    setDiscDraft('');
    loadSales();
  }

  const fxChg =
    fx && fx.history.length >= 2 && fx.history[1]!.usd_irr > 0
      ? (fx.history[0]!.usd_irr - fx.history[1]!.usd_irr) / fx.history[1]!.usd_irr
      : null;

  return (
    <>
      {/* PREPROD-1 — «سیگنال پشتیبان» is only true while the basket is actually driving. When the basket is
          starved (`factorSource === 'fx'`) the dollar IS the driver, and calling it the fallback understates
          what this register is doing to every price. State the rank the server actually used. */}
      <Card
        title={
          idx?.enabled && idx.factorSource === 'fx'
            ? 'نرخ دلار (USD/IRR) — محرک فعلی شاخص'
            : 'نرخ دلار (USD/IRR) — سیگنال پشتیبان'
        }
      >
        <div className="row" style={{ alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div className="muted" style={{ fontSize: 12 }}>
              آخرین نرخ ثبت‌شده
            </div>
            <div style={{ fontSize: 22, fontWeight: 700 }} className="num">
              {fx?.latest ? formatRial(fx.latest.usd_irr) : '—'}
              {fxChg != null ? (
                <span
                  style={{
                    fontSize: 13,
                    marginRight: 8,
                    color: fxChg >= 0 ? 'var(--red)' : 'var(--green)',
                  }}
                >
                  {fxChg >= 0 ? '▲' : '▼'} {formatPercent(Math.abs(fxChg))}
                </span>
              ) : null}
            </div>
            {fx?.latest ? (
              <div className="muted" style={{ fontSize: 11 }}>
                {fx.latest.day}
              </div>
            ) : null}
          </div>
          {canEdit ? (
            <>
              <Field label="نرخ امروز (﷼)">
                <NumberInput
                  style={{ maxWidth: 180 }}
                  value={rateDraft}
                  onValue={(n) => setRateDraft(n > 0 ? String(n) : '')}
                />
              </Field>
              <Button onClick={saveFx} loading={fxBusy} disabled={!rateDraft}>
                ثبت نرخ
              </Button>
            </>
          ) : null}
          <div style={{ marginRight: 'auto' }}>
            {fx && fx.history.length >= 2 ? (
              <Sparkline
                points={fx.history
                  .slice()
                  .reverse()
                  .map((r) => r.usd_irr)}
              />
            ) : (
              <span className="muted">با دومین ثبت، نمودار ساخته می‌شود</span>
            )}
          </div>
        </div>
        <p className="muted" style={{ marginBottom: 0 }}>
          نرخ روزانه اینجا ثبت می‌شود؛ رشد دلار به‌همراه رشد مشاهده‌شدهٔ مواد، «به‌روزرسانی شاخصی»
          را هدایت می‌کند.
        </p>
      </Card>

      {/* PREPROD-1 — the title HARDCODED «بر اساس سبد فلزات پیشران». `factorSource` is five-valued and the
          basket is starved on a thin book, so on every fx/material/none window that title asserted a driver
          the server did not use. Read the real one (and say nothing about a driver before the baseline
          enables indexation, when there is no factor at all). */}
      <Card
        title={
          idx?.enabled
            ? `به‌روزرسانی شاخصی نرخ‌های کهنه (بر اساس ${indexDriverLabel(idx.factorSource)})`
            : 'به‌روزرسانی شاخصی نرخ‌های کهنه'
        }
      >
        {!idx ? (
          <p className="muted">در حال بارگذاری…</p>
        ) : (
          <>
            {/* R1 — go-live baseline gate: indexation is OFF until the owner declares pricing complete. */}
            {!idx.enabled ? (
              <div
                style={{
                  border: '1px solid var(--line)',
                  borderInlineStart: '3px solid var(--amber)',
                  background: 'var(--amber-bg)',
                  borderRadius: 8,
                  padding: '10px 12px',
                  marginBottom: 12,
                }}
              >
                <b>شاخص‌سازی غیرفعال است — در حال تکمیل مبنای قیمت.</b>
                <p className="muted" style={{ margin: '6px 0 0' }}>
                  تا زمانی که «تاریخ مبنای قیمت» را تعیین نکنید، هیچ به‌روزرسانی شاخصی اعمال
                  نمی‌شود. این کار تضمین می‌کند ثبت‌های اولیهٔ قیمت (مبناگذاری) با نوسان واقعی بازار
                  اشتباه گرفته نشوند — فقط تغییراتِ پس از تاریخ مبنا سیگنال بازار محسوب می‌شوند.
                </p>
                {canEdit ? (
                  <Button
                    style={{ marginTop: 10 }}
                    onClick={() => void setBaseline()}
                    loading={idxBusy}
                  >
                    مبنا امروز کامل شد — فعال‌سازی شاخص
                  </Button>
                ) : null}
              </div>
            ) : (
              <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
                تاریخ مبنای قیمت: <b className="num">{idx.baselineDate}</b> — فقط تغییرات پس از این
                تاریخ به‌عنوان سیگنال بازار شمرده می‌شوند.
              </div>
            )}

            <div className="row" style={{ gap: 18, flexWrap: 'wrap' }}>
              <Field label="پنجرهٔ بررسی (روز)">
                <NumberInput
                  style={{ maxWidth: 110 }}
                  value={windowDays}
                  onValue={(n) => setWindowDays(Math.max(1, Math.min(365, n || 30)))}
                />
              </Field>
              <div>
                <div className="muted" style={{ fontSize: 12 }}>
                  رشد دلار
                </div>
                <b className="num">{idx.usdChg != null ? formatPercent(idx.usdChg) : '—'}</b>
              </div>
              <div>
                <div className="muted" style={{ fontSize: 12 }}>
                  میانگین رشد مواد
                </div>
                <b className="num">{idx.avgMatChg != null ? formatPercent(idx.avgMatChg) : '—'}</b>
              </div>
              <div>
                <div className="muted" style={{ fontSize: 12 }}>
                  میانگین ترکیبی (وزنیِ مصرف)
                </div>
                <b className="num">{idx.blended != null ? formatPercent(idx.blended) : '—'}</b>
              </div>
              <div>
                <div className="muted" style={{ fontSize: 12 }}>
                  بیشینهٔ سیگنال‌ها
                </div>
                <b className="num">{idx.maxFactor != null ? formatPercent(idx.maxFactor) : '—'}</b>
              </div>
            </div>

            {/* R1 — abnormal-data review gate: raw signal beyond the sane ceiling ⇒ auto-apply blocked. */}
            {idx.enabled && idx.abnormal ? (
              <div
                style={{
                  border: '1px solid var(--red)',
                  borderInlineStart: '3px solid var(--red)',
                  background: 'var(--red-bg)',
                  borderRadius: 8,
                  padding: '10px 12px',
                  margin: '12px 0',
                }}
              >
                <b style={{ color: 'var(--red)' }}>داده غیرعادی — بازبینی لازم.</b>
                <p className="muted" style={{ margin: '6px 0 0' }}>
                  سیگنال خام از حد مجاز فراتر رفته است؛ اعمال خودکار مسدود شد تا یک دادهٔ نادرست
                  به‌صورت ضریب چند‌برابری روی همهٔ قیمت‌ها اعمال نشود. نرخ دلار و ثبت‌های اخیر را
                  بررسی کنید، یا در صورت اطمینان «ضریب یکنواخت» را دستی وارد و اعمال کنید.
                </p>
              </div>
            ) : null}

            {/* BIDX-07 — the driver-basket that produced the tail factor. Everything here lives on the
                GROUP-PRICE plane (never effPrice/mp): base-period weights, group-level moves, and the tail
                index — the mix that would break the §2.5 identity is kept out. `weight` is within-covered
                (Σ = 1) so each leg's weight·move contribution reconciles to `basketRate` (g_B). When
                `basketRate` is null (starved/sparse basket) the whole block hides and the card degrades to
                the FX view (the fallback the server already computed into `blended`/`usdChg`). */}
            {idx.basketRate != null ? (
              <div
                style={{
                  border: '1px solid var(--line)',
                  borderInlineStart: '3px solid var(--navy)',
                  borderRadius: 8,
                  padding: '12px 14px',
                  margin: '12px 0',
                }}
              >
                <div className="row" style={{ alignItems: 'baseline', gap: 20, flexWrap: 'wrap' }}>
                  <div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      نرخ سبد پیشران — دنبالهٔ نرخ‌ها به این میزان به‌روزرسانی می‌شود
                    </div>
                    <b className="num" style={{ fontSize: 20 }}>
                      {formatPercent(idx.basketRate)}
                    </b>
                  </div>
                  <div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      ضریب گذر دنباله (β)
                    </div>
                    <b className="num">{idx.basketBeta.toFixed(2)}</b>
                  </div>
                  <div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      افزایش دنباله (β × نرخ سبد)
                    </div>
                    <b className="num">{formatPercent(idx.basketBeta * idx.basketRate)}</b>
                  </div>
                  <div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      پوشش سبد
                    </div>
                    <b className="num">
                      {idx.basketDriverCount} پیشران · {formatPercent(idx.basketCoverage)}
                    </b>
                  </div>
                </div>

                <table className="table" style={{ marginTop: 10 }}>
                  <thead>
                    <tr>
                      <th>پیشران</th>
                      <th>وزن</th>
                      <th>حرکت</th>
                      <th>سهم در نرخ سبد</th>
                      <th>آخرین تغییر</th>
                    </tr>
                  </thead>
                  <tbody>
                    {idx.basketLegs.map((leg) => (
                      <tr key={leg.key}>
                        <td>
                          {leg.name} <span className="muted">({leg.unit})</span>
                        </td>
                        <td className="num">{formatPercent(leg.weight)}</td>
                        <td
                          className="num"
                          style={{ color: leg.move >= 0 ? 'var(--red)' : 'var(--green)' }}
                        >
                          {leg.move >= 0 ? '▲' : '▼'} {formatPercent(Math.abs(leg.move))}
                        </td>
                        <td className="num">{formatPercent(leg.weight * leg.move)}</td>
                        <td className="num">{leg.lastMoveAgeDays} روز پیش</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={3} style={{ textAlign: 'end' }}>
                        <b>نرخ سبد پیشران</b>
                      </td>
                      <td className="num">
                        <b>{formatPercent(idx.basketRate)}</b>
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>

                {idx.driverStale.length ? (
                  <div style={{ marginTop: 8 }}>
                    <span
                      style={{
                        display: 'inline-block',
                        fontSize: 12,
                        color: 'var(--amber)',
                        border: '1px solid var(--amber)',
                        borderRadius: 999,
                        padding: '2px 10px',
                      }}
                    >
                      محرک کهنه: {idx.driverStale.join('، ')}
                    </span>
                  </div>
                ) : null}
              </div>
            ) : null}

            {/* R2 — the FULL stale list (replaces the count + 3 samples): see exactly what would apply.
                PREPROD-1 — «FULL» was a false claim: this listed the mp plane only, while the apply writes
                the GROUP plane in the same tx. Both planes are now listed.

                TWO LABELLED SECTIONS, not one merged list. They are different objects with different blast
                radii and no shared key: a material row is one code whose own price moves; a group row is a
                bucket «گروه|واحد» whose bump moves the costed price of EVERY material inside it that has no
                price of its own. Merging them behind a type column would put a 1-material change and a
                200-material change on visually equal rows and hide exactly the asymmetry the owner is being
                asked to approve. The headline states the union, so the number matches the apply and the bell. */}
            <p className="muted" style={{ marginTop: 12 }}>
              {idx.stale.length + idx.staleGroups.length} نرخ کهنه‌تر از پنجره است —{' '}
              {idx.stale.length} قیمت اختصاصی ماده و {idx.staleGroups.length} قیمت گروه. هر دو با یک
              «اعمال» و در یک تراکنش به‌روز می‌شوند
              {idx.enabled ? '.' : ' (پیشنهاد پس از فعال‌سازی مبنا نمایش داده می‌شود).'}
            </p>

            {idx.staleGroups.length ? (
              <>
                <p style={{ margin: '10px 0 4px', fontWeight: 700 }}>
                  قیمت گروه‌ها ({idx.staleGroups.length})
                </p>
                <p className="muted" style={{ margin: '0 0 6px', fontSize: 12 }}>
                  قیمت گروه، مبنای بهای هر مادهٔ بدون «قیمت اختصاصی» است؛ تغییر آن بهای همهٔ آن مواد
                  را جابه‌جا می‌کند. گروه‌های پیشرانِ سبد اینجا نمی‌آیند (حرکتشان قبلاً در ضریب دیده
                  شده).
                </p>
                <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>گروه</th>
                        <th>واحد</th>
                        <th>قیمت فعلی (﷼)</th>
                        <th>ضریب</th>
                        <th>قیمت پیشنهادی (﷼)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {idx.staleGroups.map((g) => {
                        // `key` is gpKey(group, unit) = «گروه|واحد» (engine.ts). Split for display the way
                        // BomModal already does; the unit half is '' for a unitless bucket.
                        const [gName, gUnit] = [
                          g.key.split('|')[0] ?? g.key,
                          g.key.split('|')[1] ?? '',
                        ];
                        return (
                          <tr key={g.key}>
                            <td>{gName || '—'}</td>
                            <td className="muted">{gUnit || '—'}</td>
                            <td className="num">{formatRial(g.price)}</td>
                            <td className="num">{idx.enabled ? formatPercent(g.factor) : '—'}</td>
                            <td className="num">
                              {idx.enabled && g.suggested !== g.price
                                ? formatRial(g.suggested)
                                : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            ) : null}

            {idx.stale.length ? (
              <>
                <p style={{ margin: '14px 0 6px', fontWeight: 700 }}>
                  قیمت اختصاصی مواد ({idx.stale.length})
                </p>
                <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>کد</th>
                        <th>گروه</th>
                        <th>قیمت فعلی (﷼)</th>
                        <th>ضریب</th>
                        <th>قیمت پیشنهادی (﷼)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {idx.stale.map((m) => (
                        <tr key={m.code}>
                          <td className="num">{m.code}</td>
                          <td>{m.group || '—'}</td>
                          <td className="num">{formatRial(m.price)}</td>
                          <td className="num">{idx.enabled ? formatPercent(m.factor) : '—'}</td>
                          <td className="num">
                            {idx.enabled && m.suggested !== m.price ? formatRial(m.suggested) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : null}

            {/* PREPROD-1 — the gate was `idx.stale.length` alone. The apply writes BOTH planes and the weekly
                bell counts BOTH, so in the group-priced book BIDX-05 targets (empty mp plane) this rendered
                NO apply button at all while the bell told the owner to press it. Gate on the union. */}
            {canEdit && idx.enabled && (idx.stale.length || idx.staleGroups.length) ? (
              <div
                className="row"
                style={{ alignItems: 'flex-end', flexWrap: 'wrap', gap: 12, marginTop: 12 }}
              >
                <Button
                  onClick={() => void applyIndexation(false)}
                  loading={idxBusy}
                  disabled={idx.abnormal}
                >
                  اعمال به‌تفکیک نرخ ({idx.stale.length + idx.staleGroups.length} نرخ)
                </Button>
                <span className="muted" style={{ fontSize: 12 }}>
                  {idx.abnormal
                    ? 'اعمال خودکار به‌دلیل دادهٔ غیرعادی مسدود است — از «ضریب یکنواخت» دستی استفاده کنید.'
                    : // PREPROD-1 — say WHICH driver (shared authority, five-valued) and that BOTH planes move.
                      `هر نرخ کهنه (ماده و گروه) با ${indexDriverLabel(idx.factorSource)} به‌روز می‌شود${
                        idx.factorSource === 'basket' ? ' (دنبالهٔ نرخ‌ها به میزان β×نرخ سبد)' : ''
                      }.`}
                </span>
                <Field
                  label="یا ضریب یکنواخت (اختیاری)"
                  hint="برای اعمال یک ضریب ثابت روی همه — مثلاً بیشینهٔ سیگنال"
                >
                  <NumberInput
                    allowDecimal
                    // A uniform factor is a fraction (0.2 = +20% → ×1.2). Bound it so a fat-fingered 35 (meant
                    // as 0.35) can't multiply every stale price by 36× — it snaps to +200%, and the confirm
                    // below still shows the resulting multiplier for a final human check (E2-03).
                    min={-0.9}
                    max={2}
                    style={{ maxWidth: 140 }}
                    value={factorDraft}
                    onValue={(n) => setFactorDraft(String(n))}
                  />
                </Field>
                <Button
                  variant="secondary"
                  onClick={() => void applyIndexation(true)}
                  loading={idxBusy}
                >
                  اعمال یکنواخت
                </Button>
              </div>
            ) : null}
          </>
        )}
      </Card>

      <Card title="لیست قیمت فروش بازار (با سقف تخفیف)">
        {canEdit ? (
          <div className="row" style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <Field label="محصول (کد یا نام)">
              {pick ? (
                <div className="row" style={{ alignItems: 'center', gap: 8, minWidth: 260 }}>
                  <b>{pick.name || pick.code}</b>
                  <span className="muted num">({pick.code})</span>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setPick(null);
                      setListDraft('');
                      setDiscDraft('');
                    }}
                  >
                    تغییر
                  </Button>
                </div>
              ) : (
                <div style={{ minWidth: 260 }}>
                  <EntityPicker<ProductHit>
                    endpoint="/api/products"
                    maxResults={8}
                    itemKey={(p) => p.code}
                    renderResult={(p) => ({
                      label: p.name || p.code,
                      subtitle: `(${p.code}) — بها ${formatRial(p.fullCost)}`,
                    })}
                    onSelect={(p) => {
                      setPick(p);
                      if (p.listPrice > 0) setListDraft(String(p.listPrice));
                      if (p.maxDiscount > 0) setDiscDraft(String(Math.round(p.maxDiscount * 100)));
                    }}
                  />
                </div>
              )}
            </Field>
            <Field label="قیمت لیست (﷼)">
              <NumberInput
                style={{ maxWidth: 170 }}
                value={listDraft}
                onValue={(n) => setListDraft(n > 0 ? String(n) : '')}
              />
            </Field>
            <Field label="حداکثر تخفیف (٪)">
              <NumberInput
                allowDecimal
                // Entered as whole percent (÷100 at save); a discount can't exceed 100% or be negative (E2-03).
                min={0}
                max={100}
                style={{ maxWidth: 120 }}
                value={discDraft}
                onValue={(n) => setDiscDraft(String(n))}
              />
            </Field>
            <Button onClick={saveSales} loading={salesBusy} disabled={!pick || !listDraft}>
              ذخیره
            </Button>
            <Button variant="secondary" onClick={() => setSalesImportOpen(true)}>
              ⬆ ورود گروهی (CSV)
            </Button>
          </div>
        ) : null}
        {canEdit && pick ? (
          <div style={{ marginTop: 8 }}>
            {/* Guidance at the list price: margin vs floor + the floor-hitting suggested price (one tap). */}
            <PricingHint
              cogs={pick.fullCost}
              price={Number(listDraft) || 0}
              floor={pick.floor}
              target={pick.target}
              onApplySuggested={(n) => setListDraft(String(n))}
            />
            {Number(listDraft) > 0 && Number(discDraft) > 0
              ? (() => {
                  // The trap the sales list hides: at the FULL discount, is the worst sellable price still
                  // above the floor? Show the discounted price + its margin.
                  const disc = Math.min(1, Math.max(0, Number(discDraft) / 100));
                  const worst = Math.round(Number(listDraft) * (1 - disc));
                  const v = pricingFor(pick.fullCost, worst, pick.floor, pick.target);
                  return (
                    <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                      با حداکثر تخفیف {formatPercent(disc)} → قیمت {formatRial(worst)}، حاشیه{' '}
                      <b style={{ color: marginColor(v.margin, floors) }}>
                        {formatPercent(v.margin)}
                      </b>
                      {v.belowFloor ? <span className="warn"> ⚠ زیر کف</span> : null}
                    </div>
                  );
                })()
              : null}
          </div>
        ) : null}
        {salesImportOpen ? (
          <CsvImportModal
            title="ورود گروهی لیست قیمت فروش (CSV)"
            hint="ستون‌ها: کد محصول، قیمت لیست، حداکثر تخفیف (٪ یا اعشاری، اختیاری)"
            previewUrl="/api/market/prices/import/preview"
            commitUrl="/api/market/prices/import"
            onClose={() => setSalesImportOpen(false)}
            onDone={() => {
              setSalesImportOpen(false);
              loadSales();
            }}
          />
        ) : null}
        {salesStatus === 'error' ? (
          <ErrorRetry error={salesError} onRetry={() => loadSales()} />
        ) : salesStatus === 'loading' ? (
          <p className="muted">در حال بارگذاری…</p>
        ) : sales.length ? (
          <table className="table" style={{ marginTop: 12 }}>
            <thead>
              <tr>
                <th>کد</th>
                <th>نام محصول</th>
                <th>قیمت لیست (﷼)</th>
                <th>سقف تخفیف</th>
                <th>به‌روزرسانی</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sales.map((s) => (
                <tr key={s.product_code}>
                  <td className="num">{s.product_code}</td>
                  <td>{s.name || '—'}</td>
                  <td className="num">{formatRial(s.list_price)}</td>
                  <td className="num">{formatPercent(s.max_discount)}</td>
                  <td className="num muted">{s.date}</td>
                  <td>
                    <Button
                      variant="ghost"
                      onClick={() =>
                        setHistoryOf({
                          kind: 'sales',
                          key: s.product_code,
                          title: s.name || s.product_code,
                        })
                      }
                    >
                      نمودار
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="muted" style={{ marginBottom: 0 }}>
            هنوز قیمتی در لیست بازار ثبت نشده — قیمت لیست، پیشنهاد قیمت هر قرارداد را هدایت می‌کند و
            فروش زیر «لیست منهای سقف تخفیف» هشدار می‌گیرد.
          </p>
        )}
      </Card>

      {historyOf ? (
        <HistoryModal
          kind={historyOf.kind}
          keyName={historyOf.key}
          title={historyOf.title}
          onClose={() => setHistoryOf(null)}
        />
      ) : null}
    </>
  );
}
